/**
 * MixedPaymentInput - Componente reutilizable para cobros mixtos (Efectivo + Tarjeta)
 *
 * Props:
 *  total        : number  – Importe total a pagar (positivo = cobrar, negativo = devolver)
 *  onChange     : fn({cashAmount, cardAmount, method, change, isValid})
 *  showPending  : bool    – Muestra botón "Pendiente"
 *  showOnline   : bool    – Muestra botón "Online"
 *  isRefund     : bool    – Modo devolución (campos readonly sugeridos)
 *  disabled     : bool    – Deshabilita todos los inputs
 *  className    : string
 */

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const MixedPaymentInput = ({
  total = 0,
  onChange,
  showPending = false,
  showOnline = false,
  isRefund = false,
  disabled = false,
  className = "",
}) => {
  const absTotal = Math.abs(total);
  
  const [mode, setMode] = useState("cash"); // cash | card | mixed | pending | online
  // Initialize cashAmount with total if > 0, otherwise empty
  const [cashAmount, setCashAmount] = useState(absTotal > 0 ? absTotal.toFixed(2) : "");
  const [cardAmount, setCardAmount] = useState("");
  const [cashGiven, setCashGiven] = useState(""); // efectivo entregado (para cambio)

  // Auto-update cash field when total changes (e.g., from parent re-render)
  useEffect(() => {
    if (absTotal > 0 && mode === "cash" && (cashAmount === "" || parseFloat(cashAmount) === 0)) {
      setCashAmount(absTotal.toFixed(2));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absTotal]);

  // Recalculate and notify parent on every change
  useEffect(() => {
    const cash = parseFloat(cashAmount) || 0;
    const card = parseFloat(cardAmount) || 0;
    const given = parseFloat(cashGiven) || 0;
    const paid = cash + card;
    const remaining = Math.max(0, absTotal - paid);

    // Change is only on the cash portion
    // If client gives more cash than what the cash part costs, they get change
    const cashNeeded = Math.max(0, absTotal - card);
    const change = given > 0 && given > cashNeeded ? given - cashNeeded : 0;

    let method;
    if (mode === "pending") method = "pending";
    else if (mode === "online") method = "pago_online";
    else if (cash > 0 && card > 0) method = "mixed";
    else if (card > 0) method = "card";
    else method = "cash";

    const isValid =
      mode === "pending"
        ? true
        : mode === "online"
        ? true
        : paid >= absTotal - 0.005;

    onChange?.({
      cashAmount: cash,
      cardAmount: card,
      cashGiven: given,
      method,
      change,
      remaining,
      isValid,
      mode,
    });
  }, [cashAmount, cardAmount, cashGiven, mode, absTotal]);

  // When mode changes, auto-fill the relevant field with the full total
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setCashGiven("");
    if (newMode === "cash") {
      setCashAmount(absTotal.toFixed(2));
      setCardAmount("");
    } else if (newMode === "card") {
      setCardAmount(absTotal.toFixed(2));
      setCashAmount("");
    } else if (newMode === "mixed") {
      // Leave empty for manual entry
      setCashAmount("");
      setCardAmount("");
    } else {
      setCashAmount("");
      setCardAmount("");
    }
  };

  const cash = parseFloat(cashAmount) || 0;
  const card = parseFloat(cardAmount) || 0;
  const given = parseFloat(cashGiven) || 0;
  const paid = cash + card;
  const remaining = Math.max(0, absTotal - paid);
  const cashNeeded = Math.max(0, absTotal - card);
  const change = given > cashNeeded && given > 0 ? given - cashNeeded : 0;
  const isOver = paid > absTotal + 0.005 && mode !== "pending" && mode !== "online";

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Mode selector */}
      <div>
        <Label className="text-sm font-semibold mb-2 block">
          {isRefund ? "Método de devolución" : "Método de pago"}
        </Label>
        <div className={`grid gap-2 ${(showPending || showOnline) ? "grid-cols-4" : "grid-cols-3"}`}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleModeChange("cash")}
            className={`h-11 rounded-md border-2 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${
              mode === "cash"
                ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
            }`}
          >
            <span className="text-base">💵</span>
            <span>Efectivo</span>
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleModeChange("card")}
            className={`h-11 rounded-md border-2 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${
              mode === "card"
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
            }`}
          >
            <span className="text-base">💳</span>
            <span>Tarjeta</span>
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleModeChange("mixed")}
            className={`h-11 rounded-md border-2 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${
              mode === "mixed"
                ? "border-purple-500 bg-purple-50 text-purple-700"
                : "border-slate-200 bg-white text-slate-600 hover:border-purple-300"
            }`}
          >
            <span className="text-base">🔀</span>
            <span>Mixto</span>
          </button>
          {showPending && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleModeChange("pending")}
              className={`h-11 rounded-md border-2 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${
                mode === "pending"
                  ? "border-amber-500 bg-amber-50 text-amber-700"
                  : "border-slate-200 bg-white text-slate-600 hover:border-amber-300"
              }`}
            >
              <span className="text-base">⏳</span>
              <span>Pendiente</span>
            </button>
          )}
          {showOnline && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleModeChange("online")}
              className={`h-11 rounded-md border-2 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${
                mode === "online"
                  ? "border-violet-500 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"
              }`}
            >
              <span className="text-base">🌐</span>
              <span>Online</span>
            </button>
          )}
        </div>
      </div>

      {/* Amount inputs */}
      {(mode === "cash" || mode === "mixed") && (
        <div className="space-y-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <div>
            <Label className="text-xs font-semibold text-emerald-700">
              💵 Efectivo ({getCurrencySymbol()})
            </Label>
            <Input
              type="number"
              value={cashAmount}
              onChange={(e) => setCashAmount(e.target.value)}
              placeholder="0.00"
              className="h-10 text-lg font-bold text-center mt-1 border-emerald-300 focus:border-emerald-500"
              min="0"
              step="0.01"
              disabled={disabled}
              autoFocus={mode === "cash"}
            />
          </div>
          {/* Efectivo entregado y cambio - solo modo efectivo o mixto */}
          {cash > 0 && (
            <div>
              <Label className="text-xs text-emerald-600">
                Efectivo entregado por el cliente ({getCurrencySymbol()})
              </Label>
              <Input
                type="number"
                value={cashGiven}
                onChange={(e) => setCashGiven(e.target.value)}
                placeholder={cash.toFixed(2)}
                className="h-8 text-sm text-center mt-1 border-emerald-200"
                min={cash}
                step="0.01"
                disabled={disabled}
              />
            </div>
          )}
          {change > 0 && (
            <div className="p-2 rounded-lg bg-blue-100 border border-blue-300">
              <p className="text-xs font-medium text-blue-700">Cambio a devolver</p>
              <p className="text-xl font-black text-blue-900">{formatCurrency(change)}</p>
            </div>
          )}
        </div>
      )}

      {(mode === "card" || mode === "mixed") && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
          <Label className="text-xs font-semibold text-blue-700">
            💳 Tarjeta ({getCurrencySymbol()})
          </Label>
          <Input
            type="number"
            value={cardAmount}
            onChange={(e) => setCardAmount(e.target.value)}
            placeholder="0.00"
            className="h-10 text-lg font-bold text-center mt-1 border-blue-300 focus:border-blue-500"
            min="0"
            step="0.01"
            disabled={disabled}
            autoFocus={mode === "card"}
          />
          {mode === "card" && (
            <p className="text-xs text-blue-600 mt-1">
              ✅ Procesa el pago con el datáfono antes de confirmar.
            </p>
          )}
        </div>
      )}

      {mode === "pending" && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-sm text-amber-700">
            ⏳ Se guardará como <strong>fiado</strong>. El importe total ({formatCurrency(absTotal)}) queda pendiente de cobro.
          </p>
        </div>
      )}

      {mode === "online" && (
        <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
          <p className="text-sm text-violet-700">🌐 Marcado como pago online / origen web.</p>
        </div>
      )}

      {/* Balance summary */}
      {mode !== "pending" && mode !== "online" && (
        <div
          className={`p-3 rounded-lg border-2 text-center ${
            remaining <= 0.005
              ? "bg-emerald-50 border-emerald-400"
              : "bg-red-50 border-red-300"
          }`}
        >
          {remaining <= 0.005 ? (
            <p className="font-bold text-emerald-700 text-lg">
              ✅ {isOver ? `Exceso: ${formatCurrency((paid - absTotal))}` : "Pagado completo"}
            </p>
          ) : (
            <p className="font-bold text-red-700 text-lg">
              ⚠️ Restante: {formatCurrency(remaining)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MixedPaymentInput;
